import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Award, Check, ChevronDown, Clock3, Lock, MapPin, Swords, Users } from "lucide-react";
import { Badge } from "@/components/atoms/Badge";
import { Button } from "@/components/atoms/Button";
import { Skeleton } from "@/components/atoms/Skeleton";
import { ScoreStepper } from "@/modules/leagues/components/ScoreStepper";
import { cn } from "@/utils/cn";
import type { Match } from "@/modules/leagues/types/match";
import type { ScoringRule } from "@/modules/leagues/types/league";
import type { RoundBonusType } from "@/modules/leagues/types/roundBonus";
import { ROUND_BONUS_LABEL } from "@/modules/leagues/types/roundBonus";
import { calculateMatchPoints } from "@/modules/scoring/ScoringEngine";
import { useParticipantPredictions, useSavePrediction, useLiveMatchState } from "@/modules/leagues/hooks/useMatches";
import { isLikelyStillLive, isStaleScheduledPastKickoff } from "@/modules/leagues/repositories/shared/matchPredictionsHelpers";
import { useToast } from "@/hooks/use-toast";

interface MatchCardProps {
  match: Match;
  leagueId: string;
  /** Regras de pontuação da liga (congeladas) — necessárias pra calcular a
   * pontuação PROVISÓRIA ao vivo com a MESMA função usada no fechamento
   * oficial (ScoringEngine) — nunca uma regra paralela (item 7). */
  scoringRules?: ScoringRule[];
  /** Total de participantes da liga — usado em "X de Y palpitaram". */
  totalParticipants?: number;
  /** Gol de Placa/Ouro configurado pro admin nesta partida específica
   * (item pedido pelo usuário: precisa aparecer na aba Palpites, não só
   * na tela de configuração — senão ninguém sabe que aquele jogo vale
   * multiplicador até a rodada fechar). `undefined`/`null` = sem bônus. */
  roundBonus?: RoundBonusType | null;
  /** Abre a tela de Detalhes da partida (pedido do usuário). Opcional —
   * sem isso, o card se comporta como antes (usado em telas onde não faz
   * sentido navegar, como o preview dentro da configuração de rodada). */
  onOpenDetail?: () => void;
  /** Abre o fluxo de criar Duelo 1x1 pra essa partida (pedido do usuário).
   * Só passar quando a liga tiver `permiteDesafios` ativo. */
  onChallenge?: () => void;
}

function TeamBadge({ name, shortName, crestUrl }: { name: string; shortName: string; crestUrl: string | null }) {
  return (
    <div className="flex flex-1 flex-col items-center gap-1.5 text-center">
      {crestUrl ? (
        <img
          src={crestUrl}
          alt={name}
          className="h-11 w-11 object-contain"
          loading="lazy"
          onError={(e) => {
            // Fallback silencioso pra iniciais se a URL do escudo falhar (Priority 14: nunca ícone falso).
            e.currentTarget.style.display = "none";
            e.currentTarget.nextElementSibling?.classList.remove("hidden");
          }}
        />
      ) : null}
      <div
        className={cn(
          "flex h-11 w-11 items-center justify-center rounded-full bg-primary-100 font-display text-xs font-bold text-primary-700 dark:bg-primary-900 dark:text-primary-200",
          crestUrl && "hidden"
        )}
      >
        {shortName}
      </div>
      <span className="line-clamp-1 text-xs font-medium">{name}</span>
    </div>
  );
}

function formatKickoff(iso: string): { date: string; time: string } {
  const d = new Date(iso);
  return {
    date: d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" }),
    time: d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }),
  };
}

export function MatchCard({ match, leagueId, scoringRules, totalParticipants, roundBonus, onOpenDetail, onChallenge }: MatchCardProps) {
  const { date, time } = useMemo(() => formatKickoff(match.kickoff), [match.kickoff]);
  const [homeInput, setHomeInput] = useState(match.userPrediction?.homeScore ?? 0);
  const [awayInput, setAwayInput] = useState(match.userPrediction?.awayScore ?? 0);
  const [showParticipants, setShowParticipants] = useState(false);
  const { toast } = useToast();
  const savePrediction = useSavePrediction(leagueId, match.round);

  const isFinished = match.status === "finished";
  const isLive = match.status === "live";

  // Placar/selo/palpite de partidas ANTIGAS presas em "scheduled"
  // (correção de EXIBIÇÃO — não mexe em predictions/participantsVisible/
  // cron/pontuação). A aba já classifica essas partidas corretamente como
  // "Encerradas" (isStaleScheduledPastKickoff), mas o CARD em si ainda
  // olhava só o status cru pra decidir o que mostrar — sem nenhuma
  // relação com a classificação da aba. Busca o placar real já gravado
  // pelo cron (liveMatchState), sem chamar a API de novo. Precisa vir
  // ANTES de canRevealParticipants/displayAsFinished serem usados abaixo.
  const isStale = isStaleScheduledPastKickoff(match);
  const { data: staleLiveState } = useLiveMatchState(leagueId, match.id, isStale);
  // "Encerrada de verdade pra fins de EXIBIÇÃO" — cobre tanto o status
  // cru já confirmado quanto a partida antiga com placar recuperado do
  // liveMatchState. Usada em TODOS os blocos que antes só olhavam
  // `isFinished` cru: selo, placar, "Seu palpite"/pts, e o botão "Ver
  // todos os palpites" — bug relatado: sem isso, o jogo de ontem mostrava
  // "Encerrada" + placar certo, mas escondia o resto porque cada bloco
  // ainda checava `isFinished` (status cru) separadamente.
  const displayAsFinished = isFinished || (isStale && Boolean(staleLiveState));
  const displayHomeScore = isStale && staleLiveState ? staleLiveState.home : match.homeScore;
  const displayAwayScore = isStale && staleLiveState ? staleLiveState.away : match.awayScore;

  // Mesmo motivo já corrigido na aba Ao vivo/Futuras: a API pode demorar
  // a atualizar o status pra "live" mesmo com a partida já rolando de
  // verdade (plano gratuito do football-data.org). Sem isto, dava pra
  // ver que os palpites já estavam travados ("jogo já começou") mas
  // ainda não dava pra ver o que os adversários palpitaram — duas partes
  // do app discordando sobre a mesma partida, de novo. Revela assim que
  // o horário real já passou — mas só dentro de uma janela razoável
  // (isLikelyStillLive), nunca indefinidamente (bug real corrigido: uma
  // partida de ontem presa em "scheduled" não pode continuar "revelando"
  // como se fosse ao vivo hoje) — MAIS `displayAsFinished`, que cobre o
  // caso da partida antiga já com placar confirmado via liveMatchState.
  const canRevealParticipants = isLive || displayAsFinished || isLikelyStillLive(match);
  const isPostponed = match.status === "postponed";
  const canPredict = match.status === "scheduled" && !match.predictionsLocked;

  const { data: participants, isLoading: loadingParticipants } = useParticipantPredictions(
    showParticipants ? leagueId : undefined,
    showParticipants ? match.id : undefined
  );

  // O palpite salvo pode chegar do Firestore DEPOIS do primeiro render
  // (query assíncrona) — sincroniza os steppers assim que o valor real
  // chegar, em vez de ficar preso no 0 x 0 inicial (Priority 2 do escopo).
  useEffect(() => {
    if (match.userPrediction) {
      setHomeInput(match.userPrediction.homeScore);
      setAwayInput(match.userPrediction.awayScore);
    }
  }, [match.userPrediction]);

  function handleSave() {
    savePrediction.mutate(
      { matchId: match.id, homeScore: homeInput, awayScore: awayInput },
      {
        onSuccess: () => toast({ title: "Palpite salvo!" }),
        onError: (err) =>
          toast({
            title: "Não foi possível salvar seu palpite",
            description: err instanceof Error ? err.message : "Tente novamente.",
            variant: "destructive",
          }),
      }
    );
  }

  const hasResult = match.homeScore !== null && match.awayScore !== null;

  // Pontuação PROVISÓRIA (ao vivo): mesma função (ScoringEngine) que o cron
  // usa no fechamento oficial — nunca uma regra paralela (item 6/7).
  const provisionalPoints = useMemo(() => {
    if (!isLive || !match.userPrediction || !hasResult || !scoringRules) return null;
    return calculateMatchPoints(
      {
        predictedHome: match.userPrediction.homeScore,
        predictedAway: match.userPrediction.awayScore,
        actualHome: match.homeScore as number,
        actualAway: match.awayScore as number,
      },
      scoringRules
    );
  }, [isLive, match.userPrediction, match.homeScore, match.awayScore, hasResult, scoringRules]);

  // Distribuição dos palpites (item 9) — só calculada com dado já liberado
  // (mesmo gate de segurança do "Ver todos os palpites"), nunca antes disso.
  const distribution = useMemo(() => {
    if (!participants || participants.length === 0) return null;
    const total = participants.length;
    let home = 0;
    let draw = 0;
    let away = 0;
    for (const p of participants) {
      if (p.homeScore > p.awayScore) home += 1;
      else if (p.homeScore < p.awayScore) away += 1;
      else draw += 1;
    }
    return {
      total,
      homePct: Math.round((home / total) * 100),
      drawPct: Math.round((draw / total) * 100),
      awayPct: Math.round((away / total) * 100),
    };
  }, [participants]);

  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-soft">
      <div className="flex items-center justify-between border-b border-border/60 px-4 py-2">
        <span className="text-[11px] font-medium text-muted-foreground">
          {date} · {time}
        </span>
        {isLive ? (
          <Badge variant="default" className="border-emerald-500/30 bg-emerald-500/15 text-emerald-700 dark:text-emerald-400">
            <span className="live-dot h-1.5 w-1.5 rounded-full bg-emerald-500 text-emerald-500" />
            Ao vivo
          </Badge>
        ) : displayAsFinished ? (
          <Badge variant="default">Encerrada</Badge>
        ) : isPostponed ? (
          <Badge variant="warning">Adiada</Badge>
        ) : (
          <Badge variant="outline">Agendada</Badge>
        )}
      </div>

      {roundBonus && (
        <div className="flex items-center justify-center gap-1.5 border-b border-accent-500/20 bg-accent-50 px-4 py-1.5 dark:bg-accent-500/10">
          <Award className="h-3.5 w-3.5 text-accent-700 dark:text-accent-300" />
          <span className="text-[11px] font-bold uppercase tracking-wide text-accent-700 dark:text-accent-300">
            {ROUND_BONUS_LABEL[roundBonus]}
          </span>
        </div>
      )}

      <div
        className={cn("flex items-center gap-3 px-4 py-4", onOpenDetail && "cursor-pointer")}
        onClick={onOpenDetail}
        role={onOpenDetail ? "button" : undefined}
        tabIndex={onOpenDetail ? 0 : undefined}
      >
        <TeamBadge name={match.homeTeam.name} shortName={match.homeTeam.shortName} crestUrl={match.homeTeam.crestUrl} />

        <div className="flex flex-col items-center gap-1">
          {isFinished || isLive || displayAsFinished ? (
            <span className="font-display text-xl font-bold tabular-nums">
              {displayHomeScore ?? 0} <span className="text-muted-foreground">×</span> {displayAwayScore ?? 0}
            </span>
          ) : (
            <span className="font-display text-sm font-semibold text-muted-foreground">×</span>
          )}
          {match.stadium && (
            <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
              <MapPin className="h-2.5 w-2.5" /> {match.stadium}
            </span>
          )}
        </div>

        <TeamBadge name={match.awayTeam.name} shortName={match.awayTeam.shortName} crestUrl={match.awayTeam.crestUrl} />
      </div>

      <div className="border-t border-border/60 px-4 py-3">
        {canPredict && (
          <div className="space-y-3">
            <div className="flex items-center justify-center gap-4">
              <ScoreStepper label={match.homeTeam.shortName} value={homeInput} onChange={setHomeInput} disabled={savePrediction.isPending} />
              <span className="mt-4 text-muted-foreground">×</span>
              <ScoreStepper label={match.awayTeam.shortName} value={awayInput} onChange={setAwayInput} disabled={savePrediction.isPending} />
            </div>
            <Button className="w-full" size="sm" isLoading={savePrediction.isPending} onClick={handleSave}>
              {match.userPrediction ? "Atualizar palpite" : "Palpitar"}
            </Button>
            {onChallenge && (
              <button
                onClick={onChallenge}
                className="flex w-full items-center justify-center gap-1.5 rounded-xl bg-accent-500 py-2.5 text-xs font-bold text-primary-900 shadow-sm transition-colors hover:bg-accent-600"
              >
                <Swords className="h-3.5 w-3.5" /> Desafiar alguém pra esse jogo
              </button>
            )}
          </div>
        )}

        {canPredict && match.userPrediction && !savePrediction.isPending && (
          <p className="mt-2 flex items-center gap-1 text-xs font-medium text-success">
            <Check className="h-3.5 w-3.5" /> Palpite enviado
          </p>
        )}

        {isPostponed && (
          <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Clock3 className="h-3.5 w-3.5" /> Partida adiada — aguardando novo horário oficial
          </p>
        )}

        {!canPredict && !displayAsFinished && !isPostponed && (
          <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Lock className="h-3.5 w-3.5" /> Palpites encerrados — a partida já começou
          </p>
        )}

        {/* SEU PALPITE vs RESULTADO ATUAL — nunca confundidos (item 5) */}
        {isLive && (
          <div className="mt-2 space-y-2">
            {match.userPrediction ? (
              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-xl bg-muted/60 px-3 py-2">
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Seu palpite</p>
                  <p className="font-display text-sm font-bold tabular-nums">
                    {match.userPrediction.homeScore} × {match.userPrediction.awayScore}
                  </p>
                </div>
                <div className="rounded-xl bg-emerald-500/10 px-3 py-2">
                  <p className="text-[10px] uppercase tracking-wide text-emerald-700 dark:text-emerald-400">Resultado atual</p>
                  <p className="font-display text-sm font-bold tabular-nums text-emerald-700 dark:text-emerald-300">
                    {match.homeScore ?? 0} × {match.awayScore ?? 0}
                  </p>
                </div>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">Você não palpitou nesta partida.</p>
            )}
            {match.userPrediction && provisionalPoints !== null && (
              <div className="flex items-center justify-between rounded-xl border border-emerald-500/30 bg-emerald-500/5 px-3 py-2">
                <span className="text-xs text-emerald-700 dark:text-emerald-300">
                  Se terminasse agora, você faria:
                </span>
                <Badge variant="success">+{provisionalPoints} pts (provisório)</Badge>
              </div>
            )}
          </div>
        )}

        {displayAsFinished && (
          <div className="space-y-2">
            {match.userPrediction ? (
              <div className="flex items-center justify-between rounded-xl bg-muted/60 px-3 py-2">
                <span className="text-xs text-muted-foreground">
                  Seu palpite: <strong className="text-foreground">{match.userPrediction.homeScore} × {match.userPrediction.awayScore}</strong>
                </span>
                <Badge variant={match.userPrediction.pointsEarned ? "success" : "default"}>
                  {match.userPrediction.pointsEarned ?? 0} pts{!match.userPrediction.isOfficial && match.userPrediction.pointsEarned !== null ? " (provisório)" : ""}
                </Badge>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">Você não palpitou nesta partida.</p>
            )}
          </div>
        )}

        {canRevealParticipants && (
          <div className="mt-2 space-y-2">
            <button
              onClick={() => setShowParticipants((v) => !v)}
              className="flex w-full items-center justify-center gap-1 rounded-lg border border-border py-2 text-xs font-medium text-primary hover:bg-primary-50 dark:hover:bg-primary-900/20"
            >
              Ver todos os palpites
              <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", showParticipants && "rotate-180")} />
            </button>

            <AnimatePresence>
              {showParticipants && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden"
                >
                  <div className="space-y-2 pt-2">
                    {loadingParticipants && (
                      <>
                        <Skeleton className="h-8 w-full" />
                        <Skeleton className="h-8 w-full" />
                      </>
                    )}

                    {!loadingParticipants && totalParticipants !== undefined && participants && (
                      <p className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                        <Users className="h-3 w-3" />
                        {participants.length} de {totalParticipants} palpitaram
                        {totalParticipants - participants.length > 0 && (
                          <span> · Falta {totalParticipants - participants.length}</span>
                        )}
                      </p>
                    )}

                    {!loadingParticipants && distribution && (
                      <div className="rounded-xl border border-border bg-muted/40 p-2.5">
                        <p className="mb-1.5 text-[11px] font-medium text-muted-foreground">
                          Distribuição dos palpites · {distribution.total} palpite{distribution.total > 1 ? "s" : ""}
                        </p>
                        <div className="space-y-1 text-[11px]">
                          <div className="flex items-center justify-between">
                            <span>Vitória {match.homeTeam.shortName}</span>
                            <span className="font-semibold">{distribution.homePct}%</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span>Empate</span>
                            <span className="font-semibold">{distribution.drawPct}%</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span>Vitória {match.awayTeam.shortName}</span>
                            <span className="font-semibold">{distribution.awayPct}%</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {participants?.map((p) => (
                      <div key={p.uid} className="flex items-center justify-between rounded-lg px-2 py-1.5 text-xs">
                        <span className="font-medium">{p.nome}</span>
                        <span className="flex items-center gap-2 tabular-nums">
                          {p.homeScore} × {p.awayScore}
                          {p.pointsEarned !== null && (
                            <Badge variant={p.pointsEarned > 0 ? "success" : "default"}>
                              {p.pointsEarned} pts{!p.isOfficial ? " (provisório)" : ""}
                            </Badge>
                          )}
                          {p.acertouPlacar && <Badge variant="success">Placar exato</Badge>}
                          {!p.acertouPlacar && p.acertouVencedor && <Badge variant="primary">Vencedor</Badge>}
                        </span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </div>
    </div>
  );
}
